import { useCallback, useState } from "react";
import Toast from "@/components/base/Toast";
import { useFavorites } from "@/hooks/useFavorites";

interface DetailActionsProps {
  favoriteKey: string;
  title: string;
  extra?: string;
}

export default function DetailActions({ favoriteKey, title, extra }: DetailActionsProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState<string | null>(null);

  const saved = isFavorite(favoriteKey);
  const closeToast = useCallback(() => setToast(null), []);

  const handleFavorite = () => {
    const nowSaved = toggleFavorite(favoriteKey);
    setToast(nowSaved ? "已加入收藏，保存在当前浏览器中" : "已取消收藏");
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
        setToast("链接已复制，可以分享给朋友了");
        return;
      }
      throw new Error("clipboard unavailable");
    } catch {
      try {
        const input = document.createElement("textarea");
        input.value = url;
        input.setAttribute("readonly", "readonly");
        input.style.position = "fixed";
        input.style.opacity = "0";
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
        setToast("链接已复制，可以分享给朋友了");
      } catch {
        setToast("复制失败，可以手动复制地址栏链接");
      }
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={handleFavorite}
        aria-pressed={saved}
        className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${
          saved
            ? "bg-accent-500 text-background-50 hover:bg-accent-600"
            : "bg-primary-500 text-foreground-950 hover:bg-primary-600"
        }`}
      >
        <i className={saved ? "ri-heart-fill text-base" : "ri-heart-line text-base"}></i>
        {saved ? "已收藏" : "收藏"}
      </button>
      <button
        type="button"
        onClick={handleShare}
        className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100"
      >
        <i className="ri-link text-base"></i>
        复制链接
      </button>
      <span className="text-xs text-foreground-400">
        {extra ?? `收藏仅保存在本机浏览器 · ${title.slice(0, 10)}…`}
      </span>
      {toast && <Toast message={toast} onClose={closeToast} />}
    </div>
  );
}